import { getCameraProperties, GENERIC_RGB_CAMERA } from '../../camera_utils'
import { getFrontendProjectType } from './upload-project'
import convex from '@turf/convex'
import area from '@turf/area'
import turfLength from '@turf/length'
import { getValidationMessage } from './validation'
import Checkbox from '../ui/elements/checkbox'
import PropTypes from 'prop-types'
import { reproject } from 'reproject'
import { compare } from '../../object-utils'
import Sqids from 'sqids'

export const initialState = {
  fieldName: '',
  crop: '',
  // Names of newly added files that we have not yet started to fetch metadata for
  newFiles: [],
  // A map of all current files, filename as key, file-data as values
  files: null,
  // A GeoJSON FeatureCollection of files we have fetched metadata for
  metaData: null,
  // Are all files currently selected
  allSelected: false,
  // The last selected captureId
  lastSelected: null,
  // The current project's id, if it has been created
  projectId: null,
  // Number of files uploaded
  uploadCount: 0,
  // The upload options available to the user
  options: [],
  // The type of multispectral sensor, if any
  cameraProperties: null,
  // Files not selected by the user that should be uploaded
  extraFiles: [],
  permissions: {},
  processingOptions: {},
  metadataProgressCount: 0,
}

export function reducer(props, state, action) {
  const { pipelineProfiles, callbacks } = props
  switch (action.type) {
    case 'RESET_FILES': {
      if (state.canAddNew) {
        const nextState = {
          ...state,
          newFiles: [],
          files: {},
          metaData: null,
          allSelected: false,
          lastSelected: null,
          cameraProperties: null,
        }

        return validate(nextState)
      } else {
        return { ...state, files: { ...state.files } }
      }
    }
    case 'BEGIN_UPDATE_METADATA':
      return {
        ...state,
        metadataProgressCount: state.metadataProgressCount + 1,
      }
    case 'FINISHED_UPDATE_METADATA':
      return {
        ...state,
        metadataProgressCount: state.metadataProgressCount - 1,
      }
    case 'UPDATE_FILES': {
      const nextUploadCount = state.uploadCount + 1
      // New images can only be added if the project hasn't already been created
      // (typically in resume or when upload fails halfway). For these cases, we have
      // already performed validation on the previous set of files, allowing new ones
      // can create an invalid state in the stored files.
      const canAddNew = state.projectId == null
      let numberDiscardedFiles = 0
      const addedFiles = action.payload

      const idMappedFiles = Object.keys(addedFiles).reduce(
        (idMap, filename) => {
          const newFile = addedFiles[filename]
          const fileId = getFileId(newFile.file)
          const currentFile = state.files?.[fileId]
          if (!currentFile) {
            if (canAddNew) {
              idMap[fileId] = { ...newFile, id: fileId }
            } else {
              numberDiscardedFiles++
            }
          } else if (!currentFile.file) {
            // This is a "missing" file from a resumed upload
            // Add it if it's not already set as done uploading
            if (currentFile.uploadStatus !== 'done') {
              idMap[fileId] = {
                ...newFile,
                ...currentFile,
                id: fileId,
              }
            }
          }

          return idMap
        },
        {}
      )

      const nextFiles = { ...state.files, ...idMappedFiles }

      const nextState = {
        ...state,
        newFiles: [...state.newFiles, ...Object.keys(idMappedFiles)],
        files: nextFiles,
        uploadCount: nextUploadCount,
        isMissingFiles: isMissingFiles(nextFiles),
        numberDiscardedFiles,
      }

      return validate(nextState)
    }
    case 'RESET_NEW_FILES':
      return { ...state, newFiles: [] }
    case 'SELECT_FILES': {
      const files = {
        ...state.files,
      }
      action.payload.forEach(
        id => (files[id] = { ...files[id], isSelected: true })
      )
      return {
        ...state,
        files,
      }
    }
    case 'UNSELECT_FILES': {
      const files = {
        ...state.files,
      }
      action.payload.forEach(
        id => (files[id] = { ...files[id], isSelected: false })
      )
      return {
        ...state,
        files,
      }
    }
    case 'REMOVE_FILES': {
      const selectedFiles = Object.values(state.files).filter(f => f.isSelected)
      const nextFiles = { ...state.files }
      selectedFiles.forEach(f => {
        delete nextFiles[f.id]
      })
      const nextFeatures = (
        (state.metaData && state.metaData.features) ||
        []
      ).filter(feature => !state.files[feature.id].isSelected)
      const nextMetaData = { type: 'FeatureCollection', features: nextFeatures }

      const nextState = {
        ...state,
        files: nextFiles,
        metaData: nextMetaData,
        captures: groupCaptures(nextMetaData),
        isMissingFiles: isMissingFiles(nextFiles),
        cameraProperties:
          nextFeatures.length > 0
            ? _getCameraProperties(nextFeatures, {
                allowMultipleCameras: state.projectType === 'individual',
              })
            : null,
        projectMeasurements: getProjectMeasurements(nextMetaData),
      }

      return validate(nextState)
    }
    case 'SET_FILE_UPLOAD_STATUS': {
      const id = action.payload.id
      if (state.files[id]) {
        return {
          ...state,
          files: {
            ...state.files,
            [id]: {
              ...state.files[action.payload.id],
              uploadStatus: action.payload.status,
            },
          },
        }
      } else {
        return state
      }
    }
    case 'SET_FILE_UPLOAD_PROGRESS': {
      const { uploadedBytes, totalBytes } = action.payload
      return {
        ...state,
        uploadedBytes,
        totalBytes,
      }
    }
    case 'UPDATE_METADATA': {
      // If a file's metadata has an error property, it means we should remove
      // that file
      const filteredFiles = { ...state.files }
      const { exif, geojson } = action.payload
      geojson.features.forEach((f, i) => {
        if (f.properties.error) {
          delete filteredFiles[f.id]
        } else {
          filteredFiles[f.id] = {
            ...filteredFiles[f.id],
          }
          if (exif && exif[i]) {
            filteredFiles[f.id].exif = exif[i]
          }
        }
      })

      const filteredFeatures = geojson.features.filter(f => !f.properties.error)

      const fileExtension =
        filteredFeatures.length &&
        filteredFeatures[0].properties.filename.split('.').pop().toLowerCase()
      const isJpeg = fileExtension === 'jpg' || fileExtension === 'jpeg'
      let options = state.options

      if (
        state.projectType !== 'geotiff' &&
        isJpeg &&
        !options.find(o => o.key === 'expressUpload')
      ) {
        options = [
          ...options,
          createOption({
            key: 'expressUpload',
            name: 'Express Upload',
            active: false,
          }),
        ]
      }

      const currentFeatures = (
        (state.metaData && state.metaData.features) ||
        []
      ).filter(f => !filteredFeatures.find(nf => nf.id === f.id))

      const nextFeatures = [
        ...currentFeatures,
        ...filteredFeatures,
      ].sort((a, b) =>
        a.properties.datetime && b.properties.datetime
          ? compare(a.properties.datetime, b.properties.datetime)
          : compare(a.properties.filename, b.properties.filename)
      )

      if (nextFeatures.length === 0) {
        const nextState = {
          ...state,
          metaData: null,
          hasMetaDataErrors:
            filteredFeatures.length !== geojson.features.length,
          files: filteredFiles,
          captures: {},
          cameraProperties: null,
          projectMeasurements: null,
        }

        return validate(nextState)
      }

      const cameraProperties = _getCameraProperties(nextFeatures, {
        allowMultipleCameras: state.projectType === 'individual',
      })

      if (
        state.projectType === 'overlapping' &&
        cameraProperties.hasDls &&
        !options.find(o => o.key === 'enableDls')
      ) {
        options = [
          ...options,
          createOption({
            key: 'enableDls',
            name: 'Use DLS for Calibration',
            active: false,
            updateProcessingOptions: project => (project.dls_enabled = true),
          }),
        ]
      }

      if (cameraProperties.imagesPerCapture === 1) {
        // Use id property to group into capture, to avoid
        // accidentally putting two separate images into the same capture
        nextFeatures.forEach(f => {
          f.properties.grouping_capture_id = f.id
        })
      } else {
        nextFeatures.forEach(f => {
          f.properties.grouping_capture_id = f.properties.capture_id
        })
      }

      const nextMetaData = { type: 'FeatureCollection', features: nextFeatures }

      const nextState = {
        ...state,
        metaData: nextMetaData,
        hasMetaDataErrors: filteredFeatures.length !== geojson.features.length,
        files: filteredFiles,
        options,
        captures: groupCaptures(nextMetaData),
        cameraProperties,
        projectMeasurements: getProjectMeasurements(nextMetaData),
        processingOptions: {
          ...state.processingOptions,
        },
      }

      return validate(nextState)
    }
    case 'SET_ALLSELECTED':
      return { ...state, allSelected: action.payload }
    case 'SET_LAST_SELECTED':
      return { ...state, lastSelected: action.payload }
    case 'UPDATE_FARM_ID': {
      const nextState = { ...state, farmId: action.payload, fieldName: '' }
      return validate(nextState)
    }
    case 'UPDATE_FIELD_NAME': {
      const nextState = { ...state, fieldName: action.payload }
      return validate(nextState)
    }
    case 'UPDATE_CROP':
      return { ...state, crop: action.payload }
    case 'BEGIN_UPLOAD':
      return { ...state, status: 'uploading' }
    case 'SET_PROJECT_ID':
      return { ...state, projectId: action.payload }
    case 'SUCCESS_UPLOAD':
      return { ...state, status: 'success' }
    case 'ERROR_UPLOAD': {
      return { ...state, status: 'error' }
    }
    case 'ABORT_UPLOAD': {
      return { ...state, status: undefined }
    }
    case 'SET_PROJECT_TYPE': {
      const projectType = action.payload
      const options = []

      if (projectType === 'overlapping' && state.permissions.create_3d_model) {
        options.push(
          createOption({
            key: 'create3dModel',
            name: 'Create 3D Model',
            active: false,
            updateProcessingOptions: options => {
              options.obj_enabled = true
              options.las_enabled = true
              if (pipelineProfiles?.['3d']) {
                Object.keys(pipelineProfiles['3d']).forEach(key => {
                  options[key] = pipelineProfiles['3d'][key]
                })
              }
            },
          })
        )
      }

      const nextState = { ...state, projectType, options }

      return validate(nextState)
    }
    case 'SET_OPTION': {
      const { key, active } = action.payload
      const nextOptions = [...state.options]
      const index = state.options.findIndex(o => o.key === key)
      const option = nextOptions[index]
      nextOptions[index] = { ...option, key, active }

      return { ...state, options: nextOptions }
    }
    case 'ADD_EXTRA_FILES': {
      const newFiles = action.payload
      const filteredDuplicates = state.extraFiles.filter(
        f => !newFiles.find(nf => nf.name === f.name)
      )
      return { ...state, extraFiles: [...filteredDuplicates, ...newFiles] }
    }
    case 'SET_PROCESSING_OPTION': {
      const { key, value } = action.payload
      return {
        ...state,
        processingOptions: { ...state.processingOptions, [key]: value },
      }
    }
    case 'SET_COST': {
      const nextState = { ...state, cost: action.payload }
      return validate(nextState)
    }
    case 'UPDATE_PERMISSIONS': {
      const nextState = { ...state, permissions: action.payload }
      return validate(nextState)
    }
    default:
      throw new Error(`Unknown action type "${action.type}".`)
  }

  function validate(state) {
    return {
      ...state,
      validationMessage: getValidationMessage(props, state, callbacks),
    }
  }
}

const sqids = new Sqids({ minLength: 10 })

export function getFileId(file) {
  const { name } = file
  const idParts = []
  for (let param of [file.lastModified, file.size]) {
    param = Number(param)
    if (isNaN(param)) throw new Error('All params must be positive numbers')
    if (param < 0) throw new Error('All params must be positive')
    do {
      idParts.push(param & 0xffff)
      param >>= 16
    } while (param > 0)
  }

  const id = sqids.encode(idParts)
  const parts = name.split('.')

  if (parts.length > 1) {
    const basename = parts.slice(0, parts.length - 1).join('.')

    return `${basename}_${id}.${parts[parts.length - 1]}`
  } else {
    return `${name}_${id}`
  }
}

export function groupCaptures(metaData) {
  const timeTolerance = 5 * 1000
  const captures = {}
  const captureTimestamps = {}
  const timestampedCaptureIds = new Map()

  for (const { properties, id } of metaData.features) {
    let timestampedCaptureId = timestampedCaptureIds.get(id)
    const {
      grouping_capture_id: groupingCaptureId,
      datetime: datetimeString,
    } = properties
    const captureId = timestampedCaptureId || groupingCaptureId
    const datetime = new Date(datetimeString).getTime()
    const timestamps = captureTimestamps[captureId]
    let timestamp
    if (timestamps) {
      const timestampsWithinTolerance = timestamps.filter(
        t => Math.abs(t - datetime) < timeTolerance
      )
      if (timestampsWithinTolerance.length > 0) {
        timestamp = timestampsWithinTolerance[0]
      } else {
        timestamp = datetime
        timestamps.push(timestamp)
      }
    } else {
      timestamp = datetime
      captureTimestamps[captureId] = [timestamp]
    }

    if (!timestampedCaptureId) {
      timestampedCaptureId = `${groupingCaptureId}_${timestamp}`
      timestampedCaptureIds.set(id, timestampedCaptureId)
      properties.final_capture_id = timestampedCaptureId
    }

    const capture = captures[timestampedCaptureId]
    if (capture) {
      capture.push(id)
    } else {
      captures[timestampedCaptureId] = [id]
    }
  }

  return captures
}

function isMissingFiles(files) {
  return (
    files &&
    Object.values(files).some(f => !f.file && f.uploadStatus !== 'done')
  )
}

function _getCameraProperties(features, options) {
  try {
    return getCameraProperties(
      features.map(feature => feature.properties),
      options
    )
  } catch (error) {
    return {
      ...GENERIC_RGB_CAMERA,
      error: `Images do not match camera: ${error.message}`,
    }
  }
}

export function createInitialState(props) {
  const {
    project,
    uploadedFiles,
    permissions,
    farmId,
    sourceImagery: metaData,
  } = props
  if (metaData) {
    // Legacy projects don't have feature ids but just filenames
    for (const feature of metaData.features) {
      if (!feature.id) {
        feature.id = feature.properties.filename
      }
    }
  }
  const cameraProperties =
    metaData && getCameraProperties(metaData.features.map(f => f.properties))
  const captures = metaData && groupCaptures(metaData)
  const files = metaData?.features.reduce((files, feature) => {
    const id = feature.id
    const uploaded = uploadedFiles.some(f => f.filename === id)
    files[id] = {
      id,
      uploadStatus: uploaded ? 'done' : null,
      uploadedMd5: uploaded && uploaded.md5,
    }
    return files
  }, {})

  const state = {
    ...initialState,
    fieldName: (project && project.fields[0] && project.fields[0].name) || '',
    farmId: (project && project.farmId) || farmId,
    crop: project && project.metadata && project.metadata.data.crop,
    metaData,
    files,
    projectId: project && project.id,
    projectType: project && getFrontendProjectType(project.project_type),
    cameraProperties,
    captures,
    isMissingFiles: isMissingFiles(files),
    permissions,
  }

  return {
    ...state,
    validationMessage: getValidationMessage(props, state),
  }
}

function getProjectMeasurements(metaData) {
  const convexHull = getHull()
  const hasConvexHull =
    convexHull &&
    convexHull.geometry &&
    convexHull.geometry.coordinates[0].length > 2
  return hasConvexHull
    ? {
        convexHull,
        epsg3857Hull: reproject(
          convexHull,
          'EPSG:4326',
          '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs'
        ),
        area: area(convexHull),
        length: turfLength(convexHull) * 1000,
      }
    : undefined

  function getHull() {
    // We've had some issues with turf sometimes throwing here,
    // try to better log the circumstances.
    try {
      return convex(metaData)
    } catch (e) {
      throw new Error(
        `Could not build convex hull: ${e}; metadata: ${JSON.stringify(
          metaData
        )}`
      )
    }
  }
}

function createOption({
  key,
  name,
  active,
  updateProject,
  updateProcessingOptions,
}) {
  return {
    key,
    Component: props => {
      return <Option {...props} name={name} id={key} />
    },
    active,
    updateProject,
    updateProcessingOptions,
  }
}

function Option({ id, name, options, disabled, onChange }) {
  return (
    <Checkbox
      id={`toggle-${id}`}
      type="toggle"
      active={options.find(o => o.key === id).active}
      onChange={e => onChange(e.target.checked)}
      disabled={disabled}>
      {name}
    </Checkbox>
  )
}

Option.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
}
